import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import config from '../config';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All status' },
  { value: 'new', label: 'New (not contacted)' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'resolved', label: 'Resolved' }
];

function stars(n) {
  const r = Math.max(1, Math.min(5, Number(n) || 0));
  return '★'.repeat(r) + '☆'.repeat(5 - r);
}

const CustomerFeedbackDashboard = () => {
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [lowOnly, setLowOnly] = useState(false);
  const [selected, setSelected] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [status, setStatus] = useState('new');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filterStatus !== 'all') params.set('status', filterStatus);
      if (lowOnly) params.set('lowOnly', 'true');

      const [statsRes, listRes] = await Promise.all([
        axios.get(`${config.API_URL}/customer-feedback/admin/stats`),
        axios.get(`${config.API_URL}/customer-feedback/admin/list?${params}`)
      ]);
      setStats(statsRes.data.stats);
      setItems(listRes.data.items || []);
    } catch (err) {
      console.error('Feedback fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [filterStatus, lowOnly]);

  useEffect(() => {
    fetchData();
    axios.post(`${config.API_URL}/admin/inbox-seen`, { type: 'feedback' }).catch(() => {});
  }, [fetchData]);

  const openItem = (item) => {
    setSelected(item);
    setAdminNotes(item.adminNotes || '');
    setStatus(item.status || 'new');
  };

  const saveItem = async () => {
    if (!selected) return;
    try {
      await axios.patch(`${config.API_URL}/customer-feedback/admin/${selected._id}`, {
        status,
        adminNotes
      });
      setSelected(null);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.message || 'Update failed');
    }
  };

  const waLink = (phone) => {
    const d = String(phone || '').replace(/\D/g, '').slice(-10);
    return `https://wa.me/91${d}`;
  };

  if (loading && !stats) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status" />
      </div>
    );
  }

  return (
    <div className="customer-feedback-page">
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div>
          <h2 className="mb-1">
            <i className="bi bi-star-half me-2 text-warning" />
            Customer Feedback
          </h2>
          <p className="text-muted mb-0">WhatsApp ratings — low scores par customer se baat karein</p>
        </div>
        <button type="button" className="btn btn-outline-primary btn-sm" onClick={fetchData}>
          <i className="bi bi-arrow-clockwise me-1" />
          Refresh
        </button>
      </div>

      {stats && (
        <div className="row g-3 mb-4">
          <div className="col-md-3 col-6">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body">
                <div className="text-muted small">Total ratings</div>
                <div className="fs-3 fw-bold">{stats.total}</div>
              </div>
            </div>
          </div>
          <div className="col-md-3 col-6">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body">
                <div className="text-muted small">Average</div>
                <div className="fs-3 fw-bold text-warning">
                  {stats.averageRating || '—'} / 5
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-3 col-6">
            <div className="card border-0 shadow-sm h-100 border-danger">
              <div className="card-body">
                <div className="text-muted small">Low (1-2) pending</div>
                <div className="fs-3 fw-bold text-danger">{stats.lowCount}</div>
              </div>
            </div>
          </div>
          <div className="col-md-3 col-6">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body">
                <div className="text-muted small">New / this week</div>
                <div className="fs-5 fw-bold">
                  {stats.newCount} <span className="text-muted fw-normal">/ {stats.weekCount} week</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="card border-0 shadow-sm mb-3">
        <div className="card-body py-3">
          <div className="row g-2 align-items-end">
            <div className="col-md-4">
              <label className="form-label small mb-1">Status</label>
              <select
                className="form-select form-select-sm"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                {STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-4">
              <div className="form-check mt-4">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="lowOnly"
                  checked={lowOnly}
                  onChange={(e) => setLowOnly(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="lowOnly">
                  Sirf low rating (1-2)
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card border-0 shadow-sm">
        <div className="card-body p-0">
          {items.length === 0 ? (
            <p className="text-muted text-center py-5 mb-0">Abhi koi feedback nahi.</p>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover mb-0 align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Date</th>
                    <th>Customer</th>
                    <th>Rating</th>
                    <th>Comment</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((row) => (
                    <tr
                      key={row._id}
                      className={row.isLowRating && row.status === 'new' ? 'table-danger' : ''}
                    >
                      <td className="small text-nowrap">
                        {new Date(row.createdAt).toLocaleString('en-IN')}
                      </td>
                      <td>
                        <div className="fw-semibold">{row.customerName || '—'}</div>
                        <div className="small text-muted">{row.phone}</div>
                      </td>
                      <td>
                        <span className="text-warning">{stars(row.rating)}</span>
                        <span className="ms-1 small">({row.rating}/5)</span>
                      </td>
                      <td className="small" style={{ maxWidth: 220 }}>
                        {row.comment || <span className="text-muted">—</span>}
                      </td>
                      <td>
                        <span
                          className={`badge ${
                            row.status === 'new'
                              ? 'bg-danger'
                              : row.status === 'contacted'
                                ? 'bg-warning text-dark'
                                : 'bg-success'
                          }`}
                        >
                          {row.status}
                        </span>
                      </td>
                      <td>
                        <div className="btn-group btn-group-sm">
                          <a
                            href={waLink(row.phone)}
                            target="_blank"
                            rel="noreferrer"
                            className="btn btn-success"
                            title="WhatsApp"
                          >
                            <i className="bi bi-whatsapp" />
                          </a>
                          <button
                            type="button"
                            className="btn btn-outline-primary"
                            onClick={() => openItem(row)}
                          >
                            Manage
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {selected && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Feedback — {selected.phone}</h5>
                <button type="button" className="btn-close" onClick={() => setSelected(null)} />
              </div>
              <div className="modal-body">
                <p>
                  <strong>Rating:</strong> {stars(selected.rating)} ({selected.rating}/5)
                </p>
                <p>
                  <strong>Comment:</strong> {selected.comment || '—'}
                </p>
                <div className="mb-3">
                  <label className="form-label">Status</label>
                  <select
                    className="form-select"
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                  >
                    <option value="new">New</option>
                    <option value="contacted">Contacted</option>
                    <option value="resolved">Resolved</option>
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label">Admin notes</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    placeholder="Call kiya, issue resolve..."
                  />
                </div>
              </div>
              <div className="modal-footer">
                <a href={waLink(selected.phone)} target="_blank" rel="noreferrer" className="btn btn-success">
                  WhatsApp customer
                </a>
                <button type="button" className="btn btn-primary" onClick={saveItem}>
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerFeedbackDashboard;
